import { Autocomplete, Box, Button, Stack, TextField, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { UpsertAction } from "../Enums";
import { DevelopmentItem, ProficiencyGraph } from "../models/dtos";
import { useErrorDisplay } from '../components/ErrorDisplay';
import BusySpinner from "../components/BusySpinner";
import { ExtractForSetError, IsEmpty, IsNotEmpty, IsNullOrUndefined } from "../Helpers";
import { I18N } from "../I18N";
import DevelopmentClient, { CreateDevelopmentItem, DevelopmentItemErrors } from "../apiClients/DevelopmentClient";
import TalentClient from "../apiClients/TalentClient";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs as DateAdapter } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import ConditionalDisplay from "../components/ConditionalDisplay";

export interface AutocompleteOption {
    label: string;
    id: string;
}

export interface CreateDevelopmentItemEx extends CreateDevelopmentItem {
    Id: string;
}

export interface DevelopmentItemUpsertComponentProps {
    mode: UpsertAction;
    title?: string;
    talentId?: string;
    developmentItemId?: string;
    proficiency?: ProficiencyGraph;
    onChange?: (item: CreateDevelopmentItemEx) => Promise<void>;
    onAdd?: (id: string) => Promise<void>;
    icon?: JSX.Element;
}

const DevelopmentItemUpsertComponent = ({ mode, title = I18N.Translate("Development Items"), talentId, developmentItemId, proficiency, onChange = async () => { }, onAdd = async () => { } }: DevelopmentItemUpsertComponentProps) => {

    const defaultItem = DevelopmentClient.CreateDefault();

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const initialized = useRef<boolean>();

    const [developmentItem, setDevelopmentItem] = useState<DevelopmentItem>(defaultItem);

    const [proficiencies, setProficiencies] = useState<AutocompleteOption[]>([]);
    const [selectedProficiency, setSelectedProficiency] = useState<AutocompleteOption>(null)
    const [localStartDate, setLocalStartDate] = useState<dayjs.Dayjs>(null);

    const [proficiencyReadOnly, setProficiencyReadOnly] = useState<boolean>(false);

    const loadData = async () => {
        try {
            setBusy(true);

            let options: AutocompleteOption[] = [];

            if (IsNotEmpty(proficiency)) {
                options.push({ label: proficiency.Name, id: proficiency.ProficiencyId })
                setSelectedProficiency(options[0]);
                setProficiencyReadOnly(true);
            } else {
                // TODO-RGK:[Refactor] move this to Development Client
                // Get Proficiencies
                const apiResponse = await new TalentClient().GetOngoingAssessment(talentId);
                if (ExtractForSetError(apiResponse, DevelopmentItemErrors.GetSingle, setError))
                    return;

                const p = apiResponse.Content.Proficiencies;
                p.forEach(p => options.push({ label: p.Name, id: p.ProficiencyId }))

                if (mode === UpsertAction.Update || mode === UpsertAction.View) {
                    const apiResponse = await new DevelopmentClient().GetSingle(developmentItemId);
                    if (ExtractForSetError(apiResponse, DevelopmentItemErrors.GetSingle, setError))
                        return;

                    setDevelopmentItem(apiResponse.Content);
                }
            }

            setProficiencies(options);
        }
        catch (error) {
            switch (mode) {
                case UpsertAction.Update:
                    setError(error, DevelopmentItemErrors.Update);
                    break;
                default:
                    setError(error, DevelopmentItemErrors.GetSingle);
                    break;
            }

        }
        finally {
            initialized.current = true;
            setBusy(false);
        }
    };

    useEffect(() => {
        if (initialized.current)
            return;

        loadData();
    })

    const handleSubmit = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                if (IsNullOrUndefined(selectedProficiency) || IsEmpty(selectedProficiency.id))
                    throw new Error("Please select a Proficiency")

                let createItem = {
                    Id: developmentItemId,
                    TalentId: talentId,
                    ProficiencyId: selectedProficiency.id,
                    Title: developmentItem.Title,
                    Narrative: developmentItem.Narrative,
                    StartDate: developmentItem.StartDate
                }

                // instead of saving the item, we should be adding it to the list of items
                // then return this list to the parent component
                onChange(createItem);
            }

        } catch (error) {
            setError(error, DevelopmentItemErrors.Create);
        }
        finally {
            setBusy(false);
        }
    };

    const handleInputChange = e => {
        const { name, value } = e.target
        setDevelopmentItem({ ...developmentItem, [name]: value })
    };

    const handleDateInputChange = (name, value: dayjs.Dayjs) => {
        setDevelopmentItem({ ...developmentItem, [name]: value })
        setLocalStartDate(value);
    };

    if (!initialized.current) {
        return <></>
    } else {
        return (
            <>
                <ErrorDisplay />
                <BusySpinner busy={busy}>
                    <Stack spacing={2}>
                        <ConditionalDisplay ifTrue={proficiencyReadOnly}>
                            <Tooltip title={proficiency.Name}>
                                <TextField label={I18N.Translate("Proficiency")} fullWidth
                                    name="Proficiency"
                                    disabled={mode === UpsertAction.View}
                                    size="small"
                                    variant="outlined"
                                    value={proficiency.Name} />
                            </Tooltip>
                        </ConditionalDisplay>
                        <ConditionalDisplay ifTrue={!proficiencyReadOnly}>
                            <Autocomplete
                                id="proficiency-autocomplete"
                                disabled={mode === UpsertAction.View}
                                options={proficiencies}
                                value={selectedProficiency}
                                onChange={(e, value) => setSelectedProficiency(value as AutocompleteOption)}
                                renderInput={(params) => <Tooltip title={params.InputLabelProps}><TextField {...params} label={I18N.Translate("Proficiency")} /></Tooltip>} />
                        </ConditionalDisplay>

                        <TextField label={I18N.Translate("Title")} fullWidth
                            name="Title"
                            disabled={mode === UpsertAction.View}
                            size="small"
                            variant="outlined"
                            error={mode !== UpsertAction.View && IsEmpty(developmentItem?.Title)}
                            value={developmentItem?.Title ?? ''}
                            onChange={handleInputChange} />

                        <TextField label="Narrative" fullWidth
                            name={I18N.Translate("Narrative")}
                            disabled={mode === UpsertAction.View}
                            size="small"
                            variant="outlined"
                            multiline
                            rows={8}
                            error={mode !== UpsertAction.View && IsEmpty(developmentItem?.Narrative)}
                            value={developmentItem?.Narrative ?? ''}
                            onChange={handleInputChange} />

                        <Box sx={{ marginTop: '1em' }}>
                            <LocalizationProvider dateAdapter={DateAdapter}>
                                <DatePicker
                                    label={I18N.Translate("Start Date")}
                                    disabled={mode === UpsertAction.View}
                                    value={localStartDate}
                                    minDate={dayjs()}
                                    onChange={(newValue) => handleDateInputChange('StartDate', newValue)}        
                                    renderInput={(params) => 
                                        <TextField 
                                            {...params} 
                                            fullWidth 
                                            disabled={mode === UpsertAction.View}
                                            error={mode !== UpsertAction.View && !localStartDate}
                                        />
                                    }
                                />
                            </LocalizationProvider>
                        </Box>
                        <ConditionalDisplay ifTrue={mode !== UpsertAction.View}>
                            <Button disabled={IsEmpty(developmentItem?.Title) || IsEmpty(developmentItem?.Narrative) || IsEmpty(localStartDate)} onClick={handleSubmit} variant='outlined' color='primary'>Submit</Button>
                        </ConditionalDisplay>
                    </Stack>
                </BusySpinner>
            </>
        )
    }
};

export default DevelopmentItemUpsertComponent;
